/**
 * Collection Media Monitor Utility
 * Monitors image and video generation status for collections using EventSource
 */

import type { CollectionMediaData } from '../types/collections.types';
import { APP_CONFIG } from '../config/app.config';

export interface MediaMonitorEvent {
  type: 'connected' | 'progress' | 'done' | 'error';
  message?: string;
  data?: CollectionMediaData;
  collectionId?: string;
  timestamp?: number;
}

export type MediaStatusCallback = (event: MediaMonitorEvent) => void;

/**
 * Monitor collection media generation status using Server-Sent Events
 * @param collectionId - The collection ID to monitor
 * @param onStatusUpdate - Callback for status updates
 * @param onError - Callback for errors
 * @returns Cleanup function to close the connection
 */
export const monitorCollectionMedia = (
  collectionId: string,
  onStatusUpdate: MediaStatusCallback,
  onError?: (error: Error) => void
): (() => void) => {
  const url = `${APP_CONFIG.MEDIAFLOW_BASE_URL}/images/task-monitor?collectionId=${collectionId}`;
  
  if (import.meta.env.DEV) {
    console.log('[Media Monitor] Starting for:', collectionId);
  }
  
  const eventSource = new EventSource(url);

  eventSource.onopen = () => {
    if (import.meta.env.DEV) console.log('[Media Monitor] Connected:', collectionId);
  };

  eventSource.addEventListener('connected', (event) => {
    try {
      const data = JSON.parse((event as MessageEvent).data);
      onStatusUpdate({
        type: 'connected',
        message: data.message,
        collectionId: data.collectionId,
        timestamp: data.timestamp,
      });
    } catch (err) {
      if (import.meta.env.DEV) console.error('[Media Monitor] Parse error (connected):', err);
    }
  });

  eventSource.addEventListener('progress', (event) => {
    try {
      const data: CollectionMediaData = JSON.parse((event as MessageEvent).data);
      onStatusUpdate({ type: 'progress', data });
    } catch (err) {
      if (import.meta.env.DEV) console.error('[Media Monitor] Parse error (progress):', err);
    }
  });

  eventSource.addEventListener('done', (event) => {
    try {
      const eventData = JSON.parse((event as MessageEvent).data);
      onStatusUpdate({
        type: 'done',
        message: eventData.message,
        data: eventData.data,
      });
      eventSource.close();
    } catch (err) {
      if (import.meta.env.DEV) console.error('[Media Monitor] Parse error (done):', err);
      eventSource.close();
    }
  });

  eventSource.addEventListener('error', () => {
    onStatusUpdate({
      type: 'error',
      message: 'Error occurred during media generation monitoring',
    });
  });

  eventSource.onerror = () => {
    if (onError) onError(new Error('Media monitor connection error'));
  };

  return () => {
    if (import.meta.env.DEV) console.log('[Media Monitor] Closing:', collectionId);
    eventSource.close();
  };
};

/** Check if media generation is complete */
export const isMediaGenerationComplete = (data: CollectionMediaData): boolean =>
  data.image_status === 'completed' && data.video_status === 'completed';

/** Check if media generation has any failures */
export const hasMediaGenerationFailed = (data: CollectionMediaData): boolean =>
  data.image_status === 'failed' || data.video_status === 'failed';

/** Get human-readable status message */
export const getMediaStatusMessage = (data: CollectionMediaData): string => {
  if (isMediaGenerationComplete(data)) return 'Image and video generation completed';
  if (hasMediaGenerationFailed(data)) {
    const failures = [];
    if (data.image_status === 'failed') failures.push('image');
    if (data.video_status === 'failed') failures.push('video');
    return `${failures.join(' and ')} generation failed`;
  }
  if (data.image_status === 'processing' || data.video_status === 'processing') {
    return 'Generating media assets...';
  }
  return 'Media generation in progress...';
};
